import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, User, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { CATEGORY_COLORS, FALLBACK_IMAGE, LOGO_URL } from "@/data/constants";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function ArticleDetail({ guest = false }) {
  const { slug, id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const req = guest ? api.get(`/guest-posts/${id}`) : api.get(`/articles/slug/${slug}`);
    req.then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug, id, guest]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = typeof window !== "undefined" ? window.location.href : "";
  const jsonLd = article
    ? [
        {
          "@context": "https://schema.org",
          "@type": guest ? "Article" : "NewsArticle",
          headline: article.title,
          description: article.excerpt,
          ...(article.image_url ? { image: [article.image_url] } : {}),
          datePublished: article.created_at,
          dateModified: article.updated_at || article.created_at,
          author: { "@type": "Person", name: guest ? article.author_name : article.author },
          publisher: {
            "@type": "Organization",
            name: "Wolfdog News",
            logo: { "@type": "ImageObject", url: LOGO_URL },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
            { "@type": "ListItem", position: 2, name: guest ? "Guest Articles" : "News", item: origin + (guest ? "/articles" : "/news") },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        },
      ]
    : null;
  useSeo({
    title: article?.title,
    description: article?.excerpt,
    image: article?.image_url,
    type: "article",
    published: article?.created_at,
    author: article ? (guest ? article.author_name : article.author) : undefined,
    jsonLd,
  });

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Article not found</h1>
        <Link to="/news" className="overline text-[#8B6D1F] mt-4 inline-block hover:underline">← Back to News</Link>
      </div>
    );
  }
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-24 text-[#4A5568]">Loading...</div>;

  const color = CATEGORY_COLORS[article.category] || "#0A192F";
  const author = guest ? article.author_name : article.author;

  return (
    <article className="pb-10" data-testid="article-detail">
      <div className="relative h-[420px] bg-[#0A192F]">
        <img src={article.image_url || FALLBACK_IMAGE} alt={article.title} className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-3xl mx-auto px-4 pb-10">
            <span className="inline-block overline text-white px-3 py-1 mb-4" style={{ backgroundColor: color }}>
              {article.category}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
              {article.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8">
        <Link to={guest ? "/articles" : "/news"} className="overline text-[#8B6D1F] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex flex-wrap items-center gap-5 mt-5 pb-6 border-b border-[#0A192F]/10 text-sm text-[#4A5568]">
          <span className="flex items-center gap-2"><User className="w-4 h-4 text-[#D4AF37]" /> {author}</span>
          <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4AF37]" /> {fmtDate(article.created_at)}</span>
        </div>

        {guest && article.author_bio && (
          <p className="mt-5 text-sm italic text-[#4A5568] bg-[#0A192F]/5 p-4 border-l-2 border-[#D4AF37]">
            About the author: {article.author_bio}
          </p>
        )}

        <div className="mt-6 max-w-none">
          {(article.content || "").split("\n").filter(Boolean).map((para, i) => {
            if (para.startsWith("@@IMG@@")) {
              const [url, caption] = para.slice(7).split("@@");
              return (
                <figure key={i} className="my-8">
                  <div className="overflow-hidden border-2 border-[#D4AF37]/50">
                    <img src={url} alt={caption || "Wolfdog"} loading="lazy" className="w-full max-h-[560px] object-cover" />
                  </div>
                  {caption && (
                    <figcaption className="mt-2 overline text-[#8B6D1F] text-center">{caption}</figcaption>
                  )}
                </figure>
              );
            }
            if (para.startsWith("@@H@@")) {
              return (
                <h2 key={i} className="font-display text-2xl font-bold tracking-tight text-[#0A192F] mt-10 mb-3">
                  {para.replace("@@H@@", "")}
                </h2>
              );
            }
            if (para.startsWith("•")) {
              return (
                <p key={i} className="text-lg text-[#1a2b45] leading-relaxed mb-2 pl-5">{para}</p>
              );
            }
            return <p key={i} className="text-lg text-[#1a2b45] leading-relaxed mb-5">{para}</p>;
          })}
        </div>

        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-[#0A192F]/10">
            {article.tags.map((t) => (
              <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="text-xs px-3 py-1.5 border border-[#0A192F]/20 rounded-full text-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-colors">
                {t}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}
